// components/Construction/RouteSelectionDialog.tsx
import React from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	List,
	ListItem,
	ListItemText,
	Typography,
	Divider,
	Box,
	Chip,
	CircularProgress
} from '@mui/material';
import DirectionsIcon from '@mui/icons-material/Directions';
import TollIcon from '@mui/icons-material/Toll';
import TimerIcon from '@mui/icons-material/Timer';
import CarIcon from '@mui/icons-material/DirectionsCar';
import TruckIcon from '@mui/icons-material/LocalShipping';

interface RouteOption {
	routeId: number;
	distance: number;
	distanceInKm: string;
	duration?: number;
	durationInMinutes?: number | null;
	tollFee: number;
	// 追加: 車種別料金
	Standard_Vehicle_Toll?: number;
	Medium_Vehicle_Toll?: number;
	Light_Vehicle_Toll?: number;
	Large_Vehicle_Toll?: number;
	roadNames?: string[];
}

interface RouteSelectionDialogProps {
	open: boolean;
	onClose: () => void;
	routes: RouteOption[];
	loading: boolean;
	onSelectRoute: (route: RouteOption) => Promise<void>; // Promise<void>に変更
	address: string;
}

export const RouteSelectionDialog: React.FC<RouteSelectionDialogProps> = ({
	open,
	onClose,
	routes,
	loading,
	onSelectRoute,
	address
   }) => {
	return (
	  <Dialog
		open={open}
		onClose={onClose}
		fullWidth
		maxWidth="md"
		aria-labelledby="route-selection-dialog-title"
	  >
		<DialogTitle id="route-selection-dialog-title">
		  経路選択
		</DialogTitle>
		<DialogContent>
		  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
			{address}
		  </Typography>
   
		  <Divider sx={{ my: 2 }} />
   
		  {loading ? (
			<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
			  <CircularProgress />
			</Box>
		  ) : routes.length === 0 ? (
			<Typography align="center" sx={{ p: 4 }}>
			  経路候補が見つかりませんでした。
			</Typography>
		  ) : (
			<List>
			  {routes.map((route, index) => (
				<React.Fragment key={route.routeId || index}>
				  {index > 0 && <Divider />}
				  <ListItem
					alignItems="flex-start"
					button
					onClick={() => onSelectRoute(route)}
					sx={{
					  p: 2,
					  '&:hover': {
						bgcolor: 'action.hover'
					  }
					}}
				  >
					<ListItemText
					  primary={
						<Typography variant="h6">
						  経路 {index + 1}
						</Typography>
					  }
					  secondaryTypographyProps={{ component: 'div' }} // <p>の代わりに<div>を使用
					  secondary={
						<Box sx={{ mt: 1 }}>
						  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
							<Chip
							  icon={<DirectionsIcon />}
							  label={`距離: ${route.distanceInKm}km`}
							  variant="outlined"
							/>
							{route.durationInMinutes && (
							  <Chip
								icon={<TimerIcon />}
								label={`所要時間: 約${route.durationInMinutes}分`}
								variant="outlined"
							  />
							)}
						  </Box>
   
						  {/* 車種別料金の表示 */}
						  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
							<Chip
							  icon={<CarIcon />}
							  label={`普通車料金: ${(route.Standard_Vehicle_Toll || route.tollFee).toLocaleString()}円`}
							  variant="outlined"
							  color="primary"
							/>
							<Chip
							  icon={<TruckIcon />}
							  label={`中型車料金: ${(route.Medium_Vehicle_Toll || route.tollFee).toLocaleString()}円`}
							  variant="outlined"
							  color="secondary"
							/>
						  </Box>
   
						  {route.roadNames && route.roadNames.length > 0 && (
							<Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
							  利用道路: {route.roadNames.join(' → ')}
							</Box>
						  )}
						</Box>
					  }
					/>
				  </ListItem>
				</React.Fragment>
			  ))}
			</List>
		  )}
		</DialogContent>
   
		<DialogActions>
		  <Button onClick={onClose} color="primary">
			キャンセル
		  </Button>
		</DialogActions>
	  </Dialog>
	);
   };

export default RouteSelectionDialog;